import React, { useRef } from 'react'
import { Controller, Control, FieldErrors, useWatch } from 'react-hook-form'
import BaseQuestion from '../../base-question'
import s from '../../styles.module.css'

interface InsuranceMembershipIdQuestionRHFProps {
  question: {
    id: number | string
    text: string
    isRequired?: boolean
    type: string
    placeholder?: string
    responses?: Array<{
      id: number | string
      text: string
    }>
  }
  control: Control<any>
  errors: FieldErrors<any>
  questionId: number
}

const InsuranceMembershipIdQuestionRHF: React.FC<
  InsuranceMembershipIdQuestionRHFProps
> = ({ question, control, errors, questionId }) => {
  const initialLoad = useRef(true)
  const skipFieldName = `skip_${question.id}`
  const skipValueRHF = useWatch({ control, name: skipFieldName })

  return (
    <BaseQuestion
      question={{
        ...question,
        id: questionId,
      }}
      control={control}
      errors={errors}>
      <Controller
        name={skipFieldName}
        control={control}
        render={({ field: skipField }) => (
          <Controller
            name={`question_${question.id}`}
            control={control}
            rules={{
              validate: question.isRequired
                ? value => {
                    // Question is complete if there's a value OR skip is checked
                    if (skipValueRHF) return true
                    if (!value || value === '') {
                      return 'Please enter a membership ID or select skip'
                    }
                    return true
                  }
                : undefined,
            }}
            render={({ field }) => {
              const handleTextBlur = (value: string) => {
                if (initialLoad.current) {
                  initialLoad.current = false
                  return
                }
                // Clear skip when user enters text
                if (value && skipField.value) {
                  skipField.onChange(false)
                }
                field.onChange(value)
              }

              const handleSkipChange = (checked: boolean) => {
                skipField.onChange(checked)
                if (checked) {
                  // Clear the text value when skip is checked
                  field.onChange(null)
                }
              }

              return (
                <div>
                  <input
                    type="text"
                    className={s.textInput}
                    value={field.value || ''}
                    onChange={e => {
                      field.onChange(e.target.value)
                      // Clear skip when user types
                      if (e.target.value && skipField.value) {
                        skipField.onChange(false)
                      }
                    }}
                    onBlur={e => handleTextBlur(e.target.value)}
                    placeholder={question.placeholder || 'Enter membership ID'}
                    disabled={skipField.value}
                  />
                  {question.responses && question.responses.length > 0 && (
                    <div className={s.checkboxContainer}>
                      {question.responses.map(response => (
                        <label key={response.id} className={s.checkboxLabel}>
                          <input
                            type="checkbox"
                            className={s.checkbox}
                            checked={skipField.value || false}
                            onChange={e => handleSkipChange(e.target.checked)}
                          />
                          <span className={s.checkboxText}>
                            {response.text}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )
            }}
          />
        )}
      />
    </BaseQuestion>
  )
}

export default InsuranceMembershipIdQuestionRHF
